import React, { useState, useEffect } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Edit, Trash2, Loader2, TrendingUp, Check, X, DollarSign, Users, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

export const AdminSubscriptionsPage = () => {
  const { api } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    max_classes: -1,
    max_instructors: -1,
    max_clients: -1,
    features: '',
    is_active: true,
    platform_fee_percentage: 0.75,
    plan_features: {
      brevo_integration: false,
      widget_embedding: false,
      mobile_app: false
    }
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const data = await apiGet('/api/admin/subscription-plans');
      setPlans(data);
    } catch (e) {
      // Try public endpoint if admin fails
      try {
        const data = await apiGet('/api/subscription-plans');
        setPlans(data);
      } catch (e2) {
        toast.error('Failed to load subscription plans');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    const payload = {
      ...form,
      features: form.features.split('\n').filter(f => f.trim()),
      platform_fee_percentage: parseFloat(form.platform_fee_percentage) || 0.75,
      plan_features: form.plan_features
    };
    
    try {
      if (isEditing && selectedPlan) {
        await apiPut('/api/admin/subscription-plans/{plan_id}', { params: { plan_id: selectedPlan.id }, body: payload });
        toast.success('Plan updated!');
      } else {
        await apiPost('/api/admin/subscription-plans', { body: payload });
        toast.success('Plan created!');
      }
      setShowDialog(false);
      resetForm();
      fetchPlans();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save plan'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Deactivate this plan? Existing subscribers will not be affected.')) return;
    try {
      await apiDelete('/api/admin/subscription-plans/{plan_id}', { params: { plan_id: planId } });
      toast.success('Plan deactivated');
      fetchPlans();
    } catch (e) {
      toast.error('Failed to deactivate plan');
    }
  };

  const openEditDialog = (plan) => {
    setSelectedPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description,
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      max_classes: plan.max_classes,
      max_instructors: plan.max_instructors,
      max_clients: plan.max_clients,
      features: plan.features.join('\n'),
      is_active: plan.is_active,
      platform_fee_percentage: plan.platform_fee_percentage || 0.75,
      plan_features: plan.plan_features || {
        brevo_integration: false,
        widget_embedding: false,
        mobile_app: false
      }
    });
    setIsEditing(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      price_monthly: 0,
      price_yearly: 0,
      max_classes: -1,
      max_instructors: -1,
      max_clients: -1,
      features: '',
      is_active: true,
      platform_fee_percentage: 0.75,
      plan_features: {
        brevo_integration: false,
        widget_embedding: false,
        mobile_app: false
      }
    });
    setIsEditing(false);
    setSelectedPlan(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-subscriptions-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Subscription Plans</h1>
            <p className="text-muted-foreground">Manage subscription tiers for studios</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Plan
          </Button>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <Card 
              key={plan.id} 
              className={`relative ${!plan.is_active ? 'opacity-60' : ''}`}
              data-testid={`plan-card-${i}`}
            >
              {!plan.is_active && (
                <Badge variant="secondary" className="absolute top-4 right-4">Inactive</Badge>
              )}
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {plan.name}
                </CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold">€{plan.price_monthly}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  or €{plan.price_yearly}/year (save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)
                </p>
                
                <div className="space-y-2 py-4 border-t border-b">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Classes
                    </span>
                    <span className="font-medium">
                      {plan.max_classes === -1 ? 'Unlimited' : plan.max_classes}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Instructors
                    </span>
                    <span className="font-medium">
                      {plan.max_instructors === -1 ? 'Unlimited' : plan.max_instructors}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Clients
                    </span>
                    <span className="font-medium">
                      {plan.max_clients === -1 ? 'Unlimited' : plan.max_clients}
                    </span>
                  </div>
                </div>

                <ul className="space-y-2">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Platform & Processing Fees */}
                <div className="pt-4 border-t space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Platform Fee</span>
                    <span className="font-medium text-foreground">{plan.platform_fee_percentage != null ? `${plan.platform_fee_percentage}%` : '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Card Processing</span>
                    <span className="font-medium text-foreground">1.3% + €0.10</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(plan)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  {plan.is_active && (
                    <Button variant="outline" size="sm" onClick={() => handleDelete(plan.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {plans.length === 0 && (
          <Card className="p-12 text-center">
            <TrendingUp className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground text-lg mb-4">No subscription plans yet</p>
            <Button onClick={() => setShowDialog(true)}>Create First Plan</Button>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Plan' : 'Create Subscription Plan'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update the details of this plan' : 'Create a new subscription tier for studios'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Plan Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                placeholder="e.g., Small Studio"
                required 
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea 
                value={form.description} 
                onChange={(e) => setForm({ ...form, description: e.target.value })} 
                placeholder="Brief description of this plan"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Price (€)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={form.price_monthly} 
                  onChange={(e) => setForm({ ...form, price_monthly: parseFloat(e.target.value) })} 
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>Yearly Price (€)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={form.price_yearly} 
                  onChange={(e) => setForm({ ...form, price_yearly: parseFloat(e.target.value) })} 
                  min={0}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Max Classes</Label>
                <Input 
                  type="number" 
                  value={form.max_classes} 
                  onChange={(e) => setForm({ ...form, max_classes: parseInt(e.target.value) })} 
                />
                <p className="text-xs text-muted-foreground">-1 = unlimited</p>
              </div>
              <div className="space-y-2">
                <Label>Max Instructors</Label>
                <Input 
                  type="number" 
                  value={form.max_instructors} 
                  onChange={(e) => setForm({ ...form, max_instructors: parseInt(e.target.value) })} 
                />
              </div>
              <div className="space-y-2">
                <Label>Max Clients</Label>
                <Input 
                  type="number" 
                  value={form.max_clients} 
                  onChange={(e) => setForm({ ...form, max_clients: parseInt(e.target.value) })} 
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Features (one per line)</Label>
              <Textarea 
                value={form.features} 
                onChange={(e) => setForm({ ...form, features: e.target.value })} 
                placeholder="Basic scheduling&#10;Client management&#10;Email notifications"
                rows={5}
              />
            </div>

            <div className="space-y-2">
              <Label>Platform Fee Percentage</Label>
              <Input 
                type="number" 
                step="0.01"
                value={form.platform_fee_percentage} 
                onChange={(e) => setForm({ ...form, platform_fee_percentage: parseFloat(e.target.value) || 0.75 })} 
                min={0}
                max={100}
              />
              <p className="text-xs text-muted-foreground">Platform commission on transactions (enter as a percentage, e.g., 0.5 for 0.5%)</p>
            </div>

            <div className="space-y-3 p-3 border rounded-lg">
              <Label className="text-sm font-medium">Plan Features (unlock capabilities)</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Brevo Integration (SMS/WhatsApp)</span>
                  <Switch 
                    checked={form.plan_features?.brevo_integration || false} 
                    onCheckedChange={(checked) => setForm({ 
                      ...form, 
                      plan_features: { ...form.plan_features, brevo_integration: checked }
                    })} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Widget Embedding</span>
                  <Switch 
                    checked={form.plan_features?.widget_embedding || false} 
                    onCheckedChange={(checked) => setForm({ 
                      ...form, 
                      plan_features: { ...form.plan_features, widget_embedding: checked }
                    })} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Mobile App Access</span>
                  <Switch 
                    checked={form.plan_features?.mobile_app || false} 
                    onCheckedChange={(checked) => setForm({ 
                      ...form, 
                      plan_features: { ...form.plan_features, mobile_app: checked }
                    })} 
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Make this plan available for purchase</p>
              </div>
              <Switch 
                checked={form.is_active} 
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} 
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isEditing ? 'Save Changes' : 'Create Plan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
